import axios from 'axios';

import type { UserPreferencesResponse } from './get-user-preferences';

export type SaveAnnotationPresetParams = {
	name: string,
	annotations: Array<Record<string, unknown>>,
	getToken: () => Promise<string>,
	setState?: (data: UserPreferencesResponse) => void,
	setError?: (error: string) => void
};

// #354: right-click "Save to presets" on a text box, shape, or multi-selection in blueprint view.
// Scoped server-side to the caller's own identity from the JWT -- no email param (see EPO).
export const saveAnnotationPreset = async (params: SaveAnnotationPresetParams): Promise<[UserPreferencesResponse | null, string | null]> => {
	const { name, annotations, getToken, setState, setError } = params;

	try {
		const token = await getToken();
		const response = await axios.post(`${import.meta.env.VITE_API_URL}/preferences/annotation-presets`, { name, annotations }, {
			headers: {
				Authorization: `Bearer ${token}`
			}
		});

		// A build of the API without the #354 routes answers 204 with an empty body, which is not an error to axios
		if (response.status !== 200 || !response.data?.annotationPresets) {
			console.error('[save-annotation-preset] unexpected response', response.status, response.data);
			const message = `Error saving preset (status ${response.status})`;
			if (setError) setError(message);
			return [null, message];
		}

		if (setState) setState(response.data);
		return [response.data, null];
	} catch (error) {
		console.error('[save-annotation-preset] request failed', error);
		if (setError) setError('Error saving preset');
		return [null, 'Error saving preset'];
	}
};

export type DeleteAnnotationPresetParams = {
	presetId: string,
	getToken: () => Promise<string>,
	setState?: (data: UserPreferencesResponse) => void,
	setError?: (error: string) => void
};

export const deleteAnnotationPreset = async (params: DeleteAnnotationPresetParams): Promise<[UserPreferencesResponse | null, string | null]> => {
	const { presetId, getToken, setState, setError } = params;

	try {
		const token = await getToken();
		const response = await axios.delete(`${import.meta.env.VITE_API_URL}/preferences/annotation-presets/${encodeURIComponent(presetId)}`, {
			headers: {
				Authorization: `Bearer ${token}`
			}
		});

		if (response.status !== 200 || !response.data?.annotationPresets) {
			console.error('[delete-annotation-preset] unexpected response', response.status, response.data);
			const message = `Error deleting preset (status ${response.status})`;
			if (setError) setError(message);
			return [null, message];
		}

		if (setState) setState(response.data);
		return [response.data, null];
	} catch (error) {
		console.error('[delete-annotation-preset] request failed', error);
		if (setError) setError('Error deleting preset');
		return [null, 'Error deleting preset'];
	}
};
